import React, { useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const GOLDEN_RATIO = (1 + Math.sqrt(5)) / 2;
const RECIPROCAL_GR = 1 / GOLDEN_RATIO;
const DURATION = RECIPROCAL_GR;

const GET_DATA = gql`
  {
    themeSettings {
      global {
        teamSubheading
        managers {
          ... on Member {
            id
            slug
            featuredImage {
              node {
                sourceUrl(size: TEAM_ARCHIVE)
                altText
              }
            }
            team {
              position
            }
            title
          }
        }
        teamQuote {
          teamAuthor
          teamText
        }
        fellows {
          ... on Member {
            id
            slug
            title
            featuredImage {
              node {
                altText
                sourceUrl(size: MEDIUM)
              }
            }
          }
        }
      }
    }
  }
`;

function Team() {
  const { loading, error, data } = useQuery(GET_DATA);

  useEffect(() => {
    if (!data) return;
    const container = document.querySelector('.main-scrolling-container');
    const main = document.querySelector('main');
    const mainInner = document.querySelector('.pageTransition__slide');
    const titles = document.querySelectorAll('.a__title');
    const titlesSibling =
      titles[0] && titles[0].parentElement.nextElementSibling;

    const tl = gsap.timeline({
      defaults: { duration: DURATION, ease: 'sine.out' },
    });

    tl.set([titles, titlesSibling], { autoAlpha: 0, translateY: 20 });

    tl.to([main, mainInner], {
      autoAlpha: 1,
      ease: 'sine.out',
    });

    tl.to([titles, titlesSibling], {
      translateY: 0,
      autoAlpha: 1,
      ease: 'sine.out',
      stagger: 0.2,
      onComplete: function () {
        ScrollTrigger.refresh();
      },
    });

    gsap.to(titles, {
      scrollTrigger: {
        trigger: titles,
        start: 'top 10%',
        end: '300%',
        scrub: true,
        scroller: container,
      },
      y: (i, target) =>
        -ScrollTrigger.maxScroll(window) * (target.dataset.speed * 0.075),
      ease: 'none',
    });
  }, [data]);

  if (loading) return <div className="h-screen w-screen"></div>;

  if (error)
    return <React.Fragment>{`Error! ${error.message}`}</React.Fragment>;

  const info = data.themeSettings.global;

  return (
    data && (
      <div className="bg-white">
        {info.teamSubheading ? (
          <div className="container">
            <h2
              className="mb-10 lg:mb-20 leading-brand-tight text-brand-11vw lg:text-brand-7xl a__title"
              data-speed=".25"
            >
              {info.teamSubheading}
            </h2>
          </div>
        ) : null}
        <div className="flex flex-wrap bg-brand-cool-grey min-h-screen">
          {info.managers &&
            info.managers.length > 0 &&
            info.managers.map(function (manager) {
              return (
                <Link
                  to={`/team/${manager.slug}`}
                  key={manager.id ? manager.id : null}
                  className="flex flex-col team-archive-member relative w-full md:w-1/2 lg:w-1/3 lg:border lg:border-white"
                >
                  <div className="bg-brand-maroon text-brand-cool-grey lg:h-full w-full leading-none flex flex-col justify-between static lg:absolute order-last lg:order-first transition duration-300 ease-out lg:opacity-0">
                    {manager.team.position ? (
                      <p className="py-brand-8 px-brand-12 border-b border-brand-cool-grey text-brand-2xl tracking-brand-tightest hidden lg:block">
                        {manager.team.position}
                      </p>
                    ) : null}
                    {manager.slug ? (
                      <h3 className="profile__name text-center lg:text-left text-brand-2xl lg:text-brand-4xl py-brand-8 px-brand-12 text-brand-cool-grey relative z-40 bg leading-none">
                        {manager.title}
                      </h3>
                    ) : null}
                    {manager.slug ? (
                      <div>
                        <p className="items-center text-brand-2xl my-brand-8 mx-brand-12 hidden lg:inline-flex brand-link brand-link--white">
                          <span>View Profile</span>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="22"
                            height="22"
                            fill="none"
                            className="ml-3"
                            viewBox="0 0 22 22"
                          >
                            <path
                              stroke="#D6E0E3"
                              strokeWidth="1.5"
                              d="M0 10.738h20.3M9.8 20.475l10.5-9.737L9.8 1"
                            ></path>
                          </svg>
                        </p>
                      </div>
                    ) : null}
                  </div>
                  {manager.featuredImage.node.sourceUrl ? (
                    <img
                      src={manager.featuredImage.node.sourceUrl}
                      alt={manager.featuredImage.node.altText}
                      className="object cover h-full w-full ml-auto"
                    />
                  ) : null}
                </Link>
              );
            })}
        </div>
        {info.teamQuote.teamText && info.teamQuote.teamAuthor && (
          <blockquote className="bg-brand-cool-grey max-w-none lg:pr-56 w-full py-20 lg:py-brand-24 text-brand-maroon container">
            <p className="text-brand-7vw lg:text-brand-6xl leading-tight mb-brand-12">
              {info.teamQuote.teamText}
            </p>
            <footer className="text-lg lg:text-brand-2xl">
              {info.teamQuote.teamAuthor}
            </footer>
          </blockquote>
        )}
        <div>
          <div className="border-b border-brand-maroon px-brand-6 lg:px-brand-12">
            <h3 className="py-brand-8 text-brand-2xl lg:text-brand-4xl">
              FELLOWS & INTERNS
            </h3>
          </div>
          <div className="flex flex-wrap">
            {info.fellows
              ? info.fellows.map(function (fellow) {
                  return fellow.id ? (
                    <div
                      key={fellow.id}
                      className="flex items-center w-full md:w-1/2 lg:w-1/4 flex lg:block"
                    >
                      <div className="team-archive-fellow flex items-center relative h-full">
                        {fellow.slug ? (
                          <Link
                            className="lg:bg-brand-maroon-50 lg:text-brand-cool-grey lg:absolute h-full w-full inline-flex justify-center items-center order-last ml-brand-6 lg:m-0 pr-6 lg:pr-0 lg:opacity-0 transition duration-300 ease-out"
                            to={`/team/${fellow.slug}`}
                          >
                            {fellow.title ? fellow.title : null}
                          </Link>
                        ) : null}
                        {fellow.featuredImage.node.sourceUrl ? (
                          <img
                            src={fellow.featuredImage.node.sourceUrl}
                            alt={fellow.featuredImage.node.altText}
                            className="object-cover h-full max-w-brand-40 lg:max-w-none lg:w-full max-h-brand-40 lg:max-h-none"
                          />
                        ) : null}
                      </div>
                    </div>
                  ) : null;
                })
              : null}
          </div>
        </div>
      </div>
    )
  );
}

export default Team;



// WEBPACK FOOTER //
// ./scripts/components/Team/Index.jsx